import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { updatePrimarySurveyor } from "requesters/jobServiceRequester"
import Select from "react-select"
import SweetAlert from "react-bootstrap-sweetalert"
import { getBaseUrl } from "../../../../reducers"

const SelectSurveyor = ({ jobService, reloadData, allocationNotice }) => {
  const {
    id,
    attributes: {
      jobId,
      draftedPrimarySurveyorId,
      suggestedSurveyors,
      surveyorsList,
      draftedPrimarySurveyorBookingValid
    }
  } = jobService
  const baseUrl = useSelector(getBaseUrl)
  const [surveyor, setSurveyor] = useState()
  const [invalidPrimarySurveyorChangeError, setInvalidPrimarySurveyorChangeError] = useState("")
  const [showError, setShowError] = useState(false)
  const [outsideHoursWarning, setOutsideHoursWarning] = useState(null)
  const [leaveConflictWarning, setLeaveConflictWarning] = useState(null)
  const [isConfirmingLeaveConflict, setIsConfirmingLeaveConflict] = useState(false)
  
  const getConfirmedLeaveConflictKey = () => `confirmed_leave_conflict_${id}`
  const [confirmedLeaveConflictSurveyorId, setConfirmedLeaveConflictSurveyorId] = useState(() => {
    if (typeof window !== 'undefined' && id) {
      const key = `confirmed_leave_conflict_${id}`
      const stored = localStorage.getItem(key)
      return stored ? parseInt(stored, 10) : null
    }
    return null
  })
  
  const setConfirmedLeaveConflict = (surveyorId) => {
    setConfirmedLeaveConflictSurveyorId(surveyorId)
    if (typeof window !== 'undefined' && id) {
      const key = `confirmed_leave_conflict_${id}`
      if (surveyorId) {
        localStorage.setItem(key, surveyorId.toString())
      } else {
        localStorage.removeItem(key)
      }
    }
  }

  const extractErrorMessage = res => {
    const errors = res?.error?.response?.data?.errors
    if (Array.isArray(errors) && errors.length > 0) {
      const outsideHoursError = errors.find(err => {
        const errorText = typeof err === "string" ? err : (err?.detail || err?.title || "")
        return typeof errorText === "string" && errorText.includes("Outside of Work Hours")
      })
      
      if (outsideHoursError) {
        return typeof outsideHoursError === "string" ? outsideHoursError : (outsideHoursError?.detail || outsideHoursError?.title || "")
      }
      
      const leaveConflictError = errors.find(err => {
        const errorText = typeof err === "string" ? err : (err?.detail || err?.title || "")
        return typeof errorText === "string" && (errorText.includes("on leave") || errorText.includes("on Leave"))
      })
      
      if (leaveConflictError) {
        return typeof leaveConflictError === "string" ? leaveConflictError : (leaveConflictError?.detail || leaveConflictError?.title || "")
      }
      
      const firstError = errors[0]
      if (typeof firstError === "string") return firstError
      if (firstError?.detail) return firstError.detail
      if (firstError?.title) return firstError.title
    }

    if (Array.isArray(res?.error?.response?.data) && res.error.response.data.length > 0) {
      return res.error.response.data[0]
    }

    return res?.error?.response?.data?.error || "Something went wrong. Please try again."
  }

  useEffect(() => {
    if (draftedPrimarySurveyorId && confirmedLeaveConflictSurveyorId && 
        draftedPrimarySurveyorId !== confirmedLeaveConflictSurveyorId &&
        draftedPrimarySurveyorId !== parseInt(confirmedLeaveConflictSurveyorId, 10)) {
      setConfirmedLeaveConflict(null)
    }
  }, [draftedPrimarySurveyorId, confirmedLeaveConflictSurveyorId])

  useEffect(() => {
    if (suggestedSurveyors) {
      let surveyorOptions = suggestedSurveyors.suggested || []
      surveyorOptions = surveyorOptions.concat(suggestedSurveyors.other || [])

      let foundSurveyor = surveyorOptions.find(surveyor => surveyor && surveyor.value === draftedPrimarySurveyorId)
      
      const currentSurveyorId = typeof draftedPrimarySurveyorId === 'string' ? parseInt(draftedPrimarySurveyorId, 10) : draftedPrimarySurveyorId
      const confirmedId = typeof confirmedLeaveConflictSurveyorId === 'string' ? parseInt(confirmedLeaveConflictSurveyorId, 10) : confirmedLeaveConflictSurveyorId
      
      if (foundSurveyor && confirmedId === currentSurveyorId) {
        foundSurveyor = {
          ...foundSurveyor,
          has_leave_conflict: true
        }
      } else if (foundSurveyor && foundSurveyor.has_leave_conflict) {
        if (foundSurveyor.has_leave_conflict && currentSurveyorId) {
          setConfirmedLeaveConflict(currentSurveyorId)
        }
      }
      
      if (!foundSurveyor && confirmedId === currentSurveyorId && currentSurveyorId) {
        const anySurveyorWithSameId = surveyorOptions.find(s => s && (s.value === currentSurveyorId || s.value === draftedPrimarySurveyorId))
        foundSurveyor = anySurveyorWithSameId ? {
          ...anySurveyorWithSameId,
          has_leave_conflict: true
        } : {
          value: currentSurveyorId,
          label: `Surveyor ${currentSurveyorId}`,
          has_leave_conflict: true
        }
      }
      
      setSurveyor(foundSurveyor || null)
    }
  }, [draftedPrimarySurveyorId, suggestedSurveyors, confirmedLeaveConflictSurveyorId])

  const reloadPage = () => {
    window.location.href = `/jobs/${jobId}/job_services/${id}`
    return window.location.href
  }

  const attemptUpdateSurveyor = (surveyorId, selectedOption, allowOutsideHours = false, allowLeaveConflict = false) => {
    const payload = {}
    if (allowOutsideHours) {
      payload.allow_outside_work_hours = true
    }
    if (allowLeaveConflict) {
      payload.allow_leave_conflict = true
    }

    updatePrimarySurveyor(baseUrl, id, surveyorId, payload)
      .then(res => {
        if (res.error) {
          const errorMessage = extractErrorMessage(res)
          const isOutsideHoursError = typeof errorMessage === "string" && errorMessage.includes("Outside of Work Hours")
          const isLeaveConflictError = typeof errorMessage === "string" && (errorMessage.includes("on leave") || errorMessage.includes("on Leave"))
          
          if (isOutsideHoursError) {
            const messageLines = errorMessage.split("\n").map(line => line.trim()).filter(Boolean)
            const [title, ...bodyLines] = messageLines
            setOutsideHoursWarning({
              surveyorId,
              selectedOption,
              title: title || "Outside of Work Hours",
              message: bodyLines.join("\n") || errorMessage
            })
            // Reset to previous value to prevent dropdown from updating
            const allOptions = [...(suggestedSurveyors?.suggested || []), ...(suggestedSurveyors?.other || [])]
            const previousSurveyor = allOptions.find(s => s.value === draftedPrimarySurveyorId)
            setSurveyor(previousSurveyor)
          } else if (isLeaveConflictError && !isConfirmingLeaveConflict && confirmedLeaveConflictSurveyorId !== surveyorId && confirmedLeaveConflictSurveyorId !== parseInt(surveyorId, 10)) {
            setLeaveConflictWarning({
              surveyorId,
              selectedOption,
              title: "Surveyor on Leave",
              message: errorMessage || "This surveyor is scheduled to be on leave during the estimated booking hours. Do you want to proceed with this assignment?"
            })
          } else {
            setInvalidPrimarySurveyorChangeError(errorMessage)
            setShowError(true)
          }
          setIsConfirmingLeaveConflict(false)
        } else if (res.status === 200) {
          setIsConfirmingLeaveConflict(false)
          
          const selectedOptionWithConflict = selectedOption ? {
            ...selectedOption,
            has_leave_conflict: allowLeaveConflict ? true : (selectedOption.has_leave_conflict || false)
          } : null
          
          if (selectedOptionWithConflict) {
            setSurveyor(selectedOptionWithConflict)
          }
          
          setTimeout(() => {
            reloadPage()
          }, 100)
        }
      })
      .catch(err => {
        setIsConfirmingLeaveConflict(false)
      })
  }

  const groupedOptions = [
    {
      label: "Suggested",
      options: suggestedSurveyors?.suggested
    },
    {
      label: "Other",
      options: suggestedSurveyors?.other
    }
  ];

  const currentSurveyorId = surveyor?.value ? (typeof surveyor.value === 'string' ? parseInt(surveyor.value, 10) : surveyor.value) : null
  const confirmedId = confirmedLeaveConflictSurveyorId ? (typeof confirmedLeaveConflictSurveyorId === 'string' ? parseInt(confirmedLeaveConflictSurveyorId, 10) : confirmedLeaveConflictSurveyorId) : null
  const hasConfirmedLeaveConflict = confirmedId !== null && currentSurveyorId === confirmedId
  const currentSurveyorHasLeaveConflict = surveyor?.has_leave_conflict || hasConfirmedLeaveConflict || false
  const currentSurveyorHasConflictingAssignments = surveyor?.has_conflicting_assignments || false
  const showOrangeWarning = !draftedPrimarySurveyorBookingValid || currentSurveyorHasLeaveConflict || currentSurveyorHasConflictingAssignments
  
  
  const controlStyle = showOrangeWarning ? { borderColor: "#ff8c00", backgroundColor: "#fffbf1" } : {}
  const singleValueStyle = showOrangeWarning ? { color: "#ff8c00", fontWeight: "bold" } : {}
  return (
    <>
      <SweetAlert
        show={!!outsideHoursWarning}
        type="warning"
        title={outsideHoursWarning?.title || "Outside of Work Hours"}
        style={{ width: "600px" }}
        showCancel
        closeOnClickOutside={false}
        allowEscape={false}
        confirmBtnText="Confirm"
        cancelBtnText="Cancel"
        onConfirm={() => {
          if (outsideHoursWarning?.surveyorId !== undefined) {
            setSurveyor(outsideHoursWarning.selectedOption)
            attemptUpdateSurveyor(outsideHoursWarning.surveyorId, outsideHoursWarning.selectedOption, true)
          }
          setOutsideHoursWarning(null)
        }}
        onCancel={() => {
          setOutsideHoursWarning(null)
          const allOptions = [...(suggestedSurveyors?.suggested || []), ...(suggestedSurveyors?.other || [])]
          const previousSurveyor = allOptions.find(s => s && s.value === draftedPrimarySurveyorId)
          setSurveyor(previousSurveyor || null)
        }}>
        {outsideHoursWarning?.message}
      </SweetAlert>
      <SweetAlert
        show={!!leaveConflictWarning}
        type="warning"
        title={leaveConflictWarning?.title || "Surveyor on Leave"}
        style={{ width: "600px" }}
        showCancel
        closeOnClickOutside={false}
        allowEscape={false}
        confirmBtnText="Confirm"
        cancelBtnText="Cancel"
        onConfirm={() => {
          if (leaveConflictWarning?.surveyorId !== undefined) {
            const surveyorId = leaveConflictWarning.surveyorId
            const savedSelectedOption = leaveConflictWarning.selectedOption
            
            setIsConfirmingLeaveConflict(true)
            setConfirmedLeaveConflict(surveyorId)
            setLeaveConflictWarning(null)
            
            const allOptions = [...(suggestedSurveyors?.suggested || []), ...(suggestedSurveyors?.other || [])]
            let selectedOption = allOptions.find(s => s && s.value === surveyorId)
            
            if (selectedOption) {
              selectedOption = {
                ...selectedOption,
                has_leave_conflict: true
              }
            } else if (savedSelectedOption) {
              selectedOption = {
                ...savedSelectedOption,
                has_leave_conflict: true
              }
            } else {
              selectedOption = {
                value: surveyorId,
                label: `Surveyor ${surveyorId}`,
                has_leave_conflict: true
              }
            }
            
            if (selectedOption) {
              setSurveyor(selectedOption)
            }
            
            attemptUpdateSurveyor(surveyorId, selectedOption, false, true)
          }
        }}
        onCancel={() => {
          setLeaveConflictWarning(null)
          const allOptions = [...(suggestedSurveyors?.suggested || []), ...(suggestedSurveyors?.other || [])]
          const previousSurveyor = allOptions.find(s => s && s.value === draftedPrimarySurveyorId)
          setSurveyor(previousSurveyor || null)
        }}>
        {leaveConflictWarning?.message}
      </SweetAlert>
      <SweetAlert
        show={showError}
        type="error"
        title="Cannot change primary surveyor!"
        style={{ width: "600px" }}
        closeOnClickOutside
        allowEscape={false}
        onConfirm={() => {
          setShowError(false)
        }}>
        {invalidPrimarySurveyorChangeError}
      </SweetAlert>
      <div className="assign mb-3">
        {allocationNotice && (
          <div className="d-flex align-items-start mb-2">
            <div className="pr-2 invisible">Primary Surveyor</div>
            <div className="assign-allocation-notice text-danger">
              {allocationNotice}
            </div>
          </div>
        )}
        <div className="d-flex align-items-center">
          <div className="pr-2">Primary Surveyor</div>
          <Select
            className="assign-dropdown"
            isClearable
            isOptionDisabled={option => option.disabled}
            isSearchable
            name="surveyor"
            onChange={e => {
              const selectedValue = e?.value || null
              
              if (e && e.has_leave_conflict) {
                setLeaveConflictWarning({
                  surveyorId: selectedValue,
                  selectedOption: e,
                  title: "Surveyor on Leave",
                  message: "This surveyor is scheduled to be on leave during the estimated booking hours. Do you want to proceed with this assignment?"
                })
                const allOptions = [...(suggestedSurveyors?.suggested || []), ...(suggestedSurveyors?.other || [])]
                const previousSurveyor = allOptions.find(s => s.value === draftedPrimarySurveyorId)
                setSurveyor(previousSurveyor)
              } else if (e && e.has_conflicting_assignments) {
                attemptUpdateSurveyor(selectedValue, e)
              } else {
                attemptUpdateSurveyor(selectedValue, e)
              }
            }}
            options={groupedOptions}
            placeholder="Select Surveyor"
            value={surveyor}
            getOptionValue={option => option.value}
            isOptionSelected={(option, selectValue) => {
              return option.value === selectValue?.value
            }}
            styles={{
              control: baseStyles => ({ ...baseStyles, ...controlStyle }),
              singleValue: baseStyles => ({ ...baseStyles, ...singleValueStyle }),
              groupHeading: base => ({
                ...base,
                fontSize: "1rem",
                fontWeight: "bold",
                textTransform: "uppercase",
              }),
              option: base => ({
                ...base,
                paddingLeft: 20,
              }),
            }} />
        </div>
      </div>
    </>
  )
}
export default SelectSurveyor
